import React, { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import confetti from 'canvas-confetti';
import { toast } from 'react-toastify';
import { ReceivingTradeItem } from '../ActiveReceivingPage';
import { AssetUrls } from 'config/assets';
import './FirstTradeCelebrationModal.css';

// Inline SVG card-back placeholder — never depends on S3/CDN, can't 403
const CARD_BACK_DATA_URI = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='223' height='311' viewBox='0 0 223 311'%3E%3Crect width='223' height='311' rx='12' fill='%231a1a2e'/%3E%3Crect x='8' y='8' width='207' height='295' rx='8' fill='none' stroke='%236F51E4' stroke-width='2' opacity='0.4'/%3E%3Ctext x='111.5' y='165' text-anchor='middle' fill='%236F51E4' font-size='48' opacity='0.5'%3E%E2%9C%A6%3C/text%3E%3C/svg%3E";

/**
 * Strict image sourcing — ONLY from trade items, never ambient state.
 * Priority: CDN (via ensureCDNUrl) > scryfallId CDN URL > card back placeholder.
 */
const getCardImageUrl = (card: ReceivingTradeItem): string => {
  // Priority 1: Use CDN via ensureCDNUrl (standard app pattern)
  if (card.image) {
    const resolved = AssetUrls.cards.ensureCDNUrl(card.image);
    if (resolved && resolved.startsWith('http')) {
      console.debug('[FirstTradeCelebration] image resolved:', { name: card.name, image: card.image, resolved });
      return resolved;
    }
    console.warn('[FirstTradeCelebration] ensureCDNUrl returned invalid URL, falling through:', { name: card.name, image: card.image, resolved });
  }
  // Priority 2: Construct CDN normal URL from scryfallId
  if (card.scryfallId) {
    const cdnUrl = AssetUrls.cards.getCardImage(card.scryfallId, 'normal');
    console.debug('[FirstTradeCelebration] using scryfallId CDN URL:', { name: card.name, scryfallId: card.scryfallId, cdnUrl });
    return cdnUrl;
  }
  // Priority 3: Card back placeholder
  console.warn('[FirstTradeCelebration] no image or scryfallId, using card back:', { name: card.name });
  return AssetUrls.cards.defaultBack;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  shareImageUrl: string | null;
  receivedCards: ReceivingTradeItem[];
}

const FirstTradeCelebrationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  shareImageUrl,
  receivedCards,
}) => {
  // Track fallback step per card index — persists across React re-renders
  const fallbackStepRef = useRef<Map<number, number>>(new Map());

  const triggerConfetti = useCallback(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const colors = ['#6F51E4', '#E02CDC', '#a78bfa', '#e879f9', '#fbbf24', '#34d399', '#f472b6'];

    confetti({
      particleCount: 80,
      spread: 70,
      origin: { x: 0.25, y: 0.5 },
      colors,
      disableForReducedMotion: true,
    });
    confetti({
      particleCount: 80,
      spread: 70,
      origin: { x: 0.75, y: 0.5 },
      colors,
      disableForReducedMotion: true,
    });
  }, []);

  useEffect(() => {
    if (isOpen) triggerConfetti();
  }, [isOpen, triggerConfetti]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleShare = async () => {
    const imageUrl = shareImageUrl;
    if (!imageUrl) {
      toast.error('Share image not available yet');
      return;
    }

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // Use Web Share API on mobile if available
      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
        const file = new File([blob], 'my-first-trade.png', { type: 'image/png' });
        await navigator.share({
          files: [file],
          text: 'Just completed my first trade on TradeMagic! 🎴 trademagic.gg',
        });
      } else {
        // Desktop: copy to clipboard
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
        toast.success('Copied to clipboard — ready to paste!');
      }
    } catch (err: any) {
      // User cancelled share dialog is not an error
      if (err?.name !== 'AbortError') {
        toast.error('Failed to share image');
      }
    }
  };

  // Get up to 3 card images for the CSS fallback fan
  const displayCards = receivedCards.slice(0, 3);
  const cardRotations = displayCards.length === 1
    ? [0]
    : displayCards.length === 2
      ? [-6, 6]
      : [-12, 0, 12];
  const cardTranslations = displayCards.length === 1
    ? [0]
    : displayCards.length === 2
      ? [-40, 40]
      : [-70, 0, 70];

  const cardFan = (
    <div className="first-trade-celebration-card-fan">
      {displayCards.map((card, i) => (
        <div
          key={i}
          className="first-trade-celebration-card"
          style={{
            transform: `rotate(${cardRotations[i]}deg) translateX(${cardTranslations[i]}px)`,
            zIndex: i === 1 ? 2 : 1,
          }}
        >
          <img
            src={getCardImageUrl(card)}
            alt={card.name}
            onError={(e) => {
              const step = fallbackStepRef.current.get(i) || 0;
              fallbackStepRef.current.set(i, step + 1);
              const initialUrl = getCardImageUrl(card);
              const fallbacks = card.scryfallId
                ? [
                    AssetUrls.cards.getCardImage(card.scryfallId, 'normal'),
                    AssetUrls.cards.getCardImage(card.scryfallId, 'small'),
                    AssetUrls.cards.getCardImage(card.scryfallId, 'large'),
                  ].filter(url => url !== initialUrl)
                : [];
              console.warn('[FirstTradeCelebration] fallback debug:', {
                name: card.name,
                src: e.currentTarget.src,
                scryfallId: card.scryfallId,
                initialUrl,
                fallbacks,
                step,
              });

              if (card.scryfallId) {

                if (step < fallbacks.length) {
                  e.currentTarget.src = fallbacks[step];
                  return;
                }
              }

              // All CDN sizes exhausted or no scryfallId — inline SVG placeholder
              e.currentTarget.src = CARD_BACK_DATA_URI;
            }}
          />
        </div>
      ))}
    </div>
  );

  return createPortal(
    <div
      className="first-trade-celebration-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="first-trade-celebration-title"
    >
      <div className="first-trade-celebration-modal">
        {/* Share image region */}
        <div className="first-trade-celebration-image-region">
          <div className="first-trade-celebration-gradient" />

          {cardFan}
        </div>

        {/* Modal chrome */}
        <div className="first-trade-celebration-content">
          <p id="first-trade-celebration-title" className="first-trade-celebration-title">
            First trade complete 🎴
          </p>
          <p className="first-trade-celebration-subtitle">
            Congratulations on your first trade! You're officially a TradeMagic Trader.
          </p>
          <div className="first-trade-celebration-actions">
            <button
              className="first-trade-celebration-btn-share"
              onClick={handleShare}
            >
              Share
            </button>
            <button
              className="first-trade-celebration-btn-continue"
              onClick={onClose}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FirstTradeCelebrationModal;
